import { AlertTriangle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { toneBadgeVariant } from "@/lib/migrations/coverage";
import { rollbackAcknowledgement, rollbackAcknowledgementReason, verificationState } from "@/lib/migrations/apply";
import type { EngineApplyAttempt } from "@/services/migrations/engine";

/**
 * WHAT THE POST-DEPLOY READ-BACK ACTUALLY ESTABLISHED, per attempt.
 *
 * "Salesforce said Succeeded" and "the org contains what we intended" are different
 * claims, and the gap between them is where a silently dropped grant lives. So the
 * verification is rendered as a verdict of its own rather than as a footnote on the deploy
 * status — and rendered with the one field that keeps it honest.
 *
 * `unread_surfaces` IS THAT FIELD. It lists the children of this artifact the org exposes
 * no readable row for: `recordTypeVisibilities` (no org-side table exists at all — three
 * candidate sObjects are 404 on a live describe) and `applicationVisibilities` (measured
 * live: 21 of 40 ids resolved through AppDefinition, so a route using it would report half
 * its members as dropped grants — a false alarm in the one signal that has to stay
 * trustworthy). Those surfaces are scoped OUT of the diff, which is correct, and it means a
 * clean diff carrying them says "everything we could READ matched" and not "everything
 * matched".
 *
 * WITHOUT THIS ON SCREEN, `identical` READS AS THE SECOND SENTENCE. An empty
 * `unread_surfaces` is the only case an applied outcome may be read as "the org contains
 * what we intended" without a caveat — which is exactly what makes the caveat meaningful
 * when it does appear, and worthless if it is never shown.
 *
 * IT ALSO NAMES WHICH ACKNOWLEDGEMENT A ROLLBACK WOULD NEED. `acknowledge_unverified` and
 * `acknowledge_mismatch` are separate flags on the rollback body and they are not
 * interchangeable: an unverified apply has no diff, so "I have seen the diff and accept
 * it" is a sentence that is not true about it. The server keeps them distinct on purpose;
 * a screen that blurred them would be teaching a reader the wrong thing about what they
 * are about to assert.
 *
 * PURE: props in, markup out.
 */
export function EngineVerificationPanel({ attempt }: { attempt: EngineApplyAttempt }) {
  const state = verificationState(attempt.verification);
  const acknowledgement = rollbackAcknowledgement(attempt);
  const acknowledgementReason = rollbackAcknowledgementReason(acknowledgement);
  const unread = state.unreadSurfaces;

  return (
    <div className="space-y-1.5" data-testid="verification">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          read back
        </span>
        <Badge variant={toneBadgeVariant(state.tone)}>{state.code.toLowerCase()}</Badge>
        {/* THE COUNT, beside the verdict and never behind a disclosure. A verification
            that compared less than the whole document says so in the same glance as the
            verdict it qualifies. */}
        {unread.length > 0 ? (
          <Badge
            variant="warm"
            title="Surfaces the org exposes no readable row for. They were NOT compared."
          >
            {unread.length} surface(s) not compared
          </Badge>
        ) : null}
      </div>

      <p className="flex items-start gap-1.5 max-w-prose text-xs leading-relaxed">
        {state.code === "VERIFIED_WHOLE" ? (
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
        ) : state.code === "VERIFIED_PARTIAL" ? (
          <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--accent-warm))]" />
        ) : state.code === "NOT_RECORDED" ? (
          <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
        )}
        <span className="min-w-0 flex-1 text-foreground">{state.headline}</span>
      </p>

      {unread.length > 0 ? (
        <div className="space-y-0.5 rounded-md border border-[hsl(var(--accent-warm))]/40 bg-accent-warm-soft p-2 text-[11px] text-accent-warm">
          <p className="max-w-prose leading-relaxed" data-testid="unread-surfaces">
            The org exposes no readable row for{" "}
            <span className="font-mono">{unread.join(", ")}</span>, so{" "}
            {unread.length > 1 ? "they were" : "it was"} not compared. A match above covers
            everything else and not these.
          </p>
        </div>
      ) : null}

      {state.detail ? (
        <p className="max-w-prose text-[11px] leading-relaxed text-muted-foreground">
          {state.detail}
        </p>
      ) : null}

      {acknowledgementReason ? (
        <p className="max-w-prose text-[11px] leading-relaxed text-muted-foreground">
          <span className="font-mono uppercase">{acknowledgement}: </span>
          {acknowledgementReason}
        </p>
      ) : null}
    </div>
  );
}
